import _ from 'lodash';

import { roles } from '../../../features/roles/role.constants';
import { hasCurrentUserPermissions } from '../../../features/roles/role.selectors';
import { getSelectedApplication } from '../../../selectors/applications/applications';
import { getSelectedOrganizationId } from '../../../selectors/organizations/organizations';
import { Project } from '../../../types/entities';
import { RouterContext } from '../../../types/router';
import { loadSdk } from '../../files/files.actions';
import { loadApplicationGroups } from '../../permissions/permissions.actions';
import {
  getDefaultProjectId,
  getProjects,
} from '../../projects/projects.selectors';
import { Dispatch, GetState } from '../../redux.types';
import {
  getApplication,
  getApplications,
  getApplications as getApplicationsAction,
} from '../application';
import {
  getProjects as getProjectsAction,
  getProjectsCached,
} from '../project';

import { buildLoadDataCachedFn, fetchDataOnProjectChange } from './utils/utils';

const loadData = (context, dispatch, getState) => {
  const state = getState();
  const organizationId = getSelectedOrganizationId(state, { router: context });

  if (!organizationId) {
    return Promise.reject({
      code: 404,
    });
  }

  return getProjectsCached(organizationId)(dispatch, getState).then(() => {
    const updatedState = getState();
    const projectId = getDefaultProjectId(updatedState, { router: context });

    if (projectId) {
      return getApplicationsAction({ organizationId, projectId })(dispatch);
    }
  });
};

const loadDataCached = buildLoadDataCachedFn(
  loadData,
  fetchDataOnProjectChange,
);

export const getApplicationsData =
  (context: RouterContext) => (dispatch, getState) => {
    return loadDataCached(context, dispatch, getState);
  };

function loadApplicationGroupsByPermissions(permissions, dispatch, getState) {
  return permissions ? loadApplicationGroups()(dispatch, getState) : null;
}

interface ApplicationOverviewContextParams {
  organizationId: string;
  projectId: string;
  applicationId: string;
}

export const loadApplicationOverviewData =
  (context: RouterContext<ApplicationOverviewContextParams>) =>
  (dispatch: Dispatch, getState: GetState) => {
    return getApplicationsData(context)(dispatch, getState).then(() => {
      const state = getState();
      const selectedApplication = getSelectedApplication(state, context);
      const organizationId = getSelectedOrganizationId(state, context);
      const projects = getProjects(state);
      const hasUserApplicationPermissionAddFromGroupPermissions =
        hasCurrentUserPermissions(state)(
          roles.applications.application.permission.addFromGroup,
        );

      if (!organizationId) {
        return Promise.reject({
          code: 404,
        });
      }

      if (selectedApplication) {
        return loadApplicationGroupsByPermissions(
          hasUserApplicationPermissionAddFromGroupPermissions,
          dispatch,
          getState,
        );
      }

      if (!projects.length) {
        return Promise.all([
          getProjectsAction(organizationId)(dispatch),
          loadApplicationGroupsByPermissions(
            hasUserApplicationPermissionAddFromGroupPermissions,
            dispatch,
            getState,
          ),
          getApplications(context.params)(dispatch),
        ]).then((data) => {
          const [projects] = data;
          const selectedProject = _.find<Project>(projects, {
            id: context.params.projectId,
          });
          return loadSdk(organizationId, selectedProject);
        });
      }

      return getApplication(context.params)(dispatch);
    });
  };
